'use client';

import {
  Experiment,
  type ExperimentClient,
  type ExperimentUser,
} from '@amplitude/experiment-js-client';
import { datadogRum } from '@datadog/browser-rum';
import {
  DEPLOYMENT_DOMAIN,
  NEXT_PUBLIC_AMPLITUDE_EXPERIMENTS_KEY,
  NEXT_PUBLIC_RELEASE_VERSION,
} from '@env';
import { auth } from '@modules/firebase';
import { createChildLogger } from '@modules/logger';
import { getCookie } from 'cookies-next';
import { onIdTokenChanged } from 'firebase/auth';
import {
  type FC,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { getOrSetViewerId } from './analytics/helpers';
import {
  FLAG_OVERRIDE_COOKIE,
  flagOverridesEnabled,
  getEffectiveOverrides,
} from './feature-flag-overrides';
import type { UserDetails } from './user-profile/user-profile-details-validator';

export interface FeatureFlagVariant {
  example: 'control' | 'variant';
  'public-api-keys': 'off' | 'on';
  'wallet-on-dashboard': 'off' | 'on';
  'stytch-password': 'off' | 'on';
  'mobile-signup-direct-link': 'control' | 'treatment';
  'outlook-contact-sync': 'control' | 'treatment';
  outlook_crm_workflow_v2: 'on' | 'off';
  'hubspot-v2-enabled': 'off' | 'on';
  'salesforce-v2-enabled': 'off' | 'on';
  'ms-dynamics-sales-v2-enabled': 'off' | 'on';
  'gohighlevel-crm-enabled': 'off' | 'on';
  'zoho-v2-native': 'off' | 'on';
  'monday-crm-enabled': 'off' | 'on';
  'pipedrive-native-enabled': 'off' | 'on';
  'followupboss-native-enabled': 'off' | 'on';
  'assign-multiple-profiles-for-cards': 'off' | 'on';
  'directory-sync-source-of-truth': 'off' | 'on';
  'entra-photo-refresh': 'off' | 'on';
  'quick-create-cards-templates': 'off' | 'on';
  'onboarding-paywall-uplift': 'control' | 'treatment';
  'hide-business-onboarding-interval-toggle': 'on' | 'off';
  'business-paywall-annual-billing-only': 'control' | 'treatment';
  'create-template-from-card': 'on' | 'off';
  'salesforce-contact-export': 'on' | 'off';
  'individual-onboarding-wizard-iteration-3': 'control' | 'treatment';
  blinq_logo_in_qr: 'control' | 'treatment';
  'integrations-glow-up': 'on' | 'off';
  ios_download_app: 'control' | 'treatment';
  qr_link_subdomains: 'control' | 'share' | 'share1' | 'share2' | 'share3' | 'fallback' | 'off';
  'new-team-dash-home': 'off' | 'on';
  'share-contact-with-business': 'off' | 'on';
  web_churnbuster: 'off' | 'on';
  'web-ia-new-templates-landing-page': 'off' | 'on';
  schrodinger_delete_contact: 'off' | 'on';
  'web-ia-new-team-cards-page': 'off' | 'on';
  schrodinger_edit_card: 'off' | 'on';
  'web-ia-team-members-page-redesign': 'off' | 'on';
  schrodinger_virtual_background_image: 'off' | 'on';
  schrodinger_virtual_background_delete: 'off' | 'on';
  schrodinger_create_update_user_doc: 'off' | 'on';
  'web-contact-details-page-redesign': 'off' | 'on';
  'dynamic-link-service-webapp': 'off' | 'on';
  zaraz_analytics_off: 'off' | 'on';
  amplitude_analytics_sdk: 'off' | 'on';
  'new-team-member-invite-flow': 'off' | 'on';
  'small-business-contact-sales-demo': 'control' | 'treatment';
  contact_enrichment: 'off' | 'on';
  'list-enrichment': 'off' | 'on';
  'list-enrichment-v2': 'off' | 'on';
  dashboard_workspace_global_contact_tags: 'off' | 'on';
  'team-member-invite-dialog-variant': 'control' | 'treatment';
  scribe_for_business_contacts: 'off' | 'on';
  scribe_disable_oac_toggle: 'off' | 'on';
  'admin-controlled-ai-features': 'off' | 'on';
  company_directory: 'off' | 'on';
  'workspace-discovery': 'off' | 'on';
  web_analytics_dashboard: 'off' | 'on';
  allow_domains_for_link_invites: 'off' | 'on';
  dash_custom_shareback_form: 'off' | 'on';
  'web-new-homepage': 'off' | 'on';
  'create-contact-from-badge-scan': 'off' | 'on';
  'dashboard-add-contacts-to-campaigns': 'off' | 'on';
  'manual-crm-triaging': 'off' | 'on';
  'custom-form-in-campaigns': 'off' | 'on';
  'calendar-booking-in-lead-form': 'off' | 'on';
  'lead-capture-metadata': 'off' | 'on';
  'disable-shareback': 'off' | 'on';
  'blinq-contact-owner-setting': 'off' | 'on';
  'show-all-salesforce-fields-in-the-field-mapper': 'off' | 'on';
  'mapping-enriched-fields': 'off' | 'on';
  'unlimited-fields-on-custom-share-back-form': 'off' | 'on';
  'expanded-dropdown-options-on-custom-share-back-form': 'off' | 'on';
  web_marketo_integration: 'off' | 'on';
  'campaigns-v2': 'off' | 'on';
  'campaigns-single-page-create': 'off' | 'on';
  'campaign-insights': 'off' | 'on';
  'web-unified-profile-phase-1': 'off' | 'on';
  'web-unified-profile-phase-2': 'off' | 'on';
  'new-signup-and-onboarding': 'on' | 'off';
  'business-signup-and-onboarding': 'on' | 'off';
  'cello-referral-paywall': 'off' | 'on';
  'bulk-team-member-dialog': 'off' | 'on';
  'usage-based-billing-on-dashboard': 'off' | 'on';
  web_signup_identity_editor: 'control' | 'off' | 'on';
}

interface FeatureFlagMetaData {
  /**
   * Waits for client to initialize and fetch the flag
   */
  loading: boolean;
  /**
   * Exposes the user to the flag, will not fire unless state is ready.
   */
  expose: () => void;
}

const flagLogger = createChildLogger({ module: 'feature-flag' });

// Cap how long a flag fetch may block the loading state before we abandon to the fallback — Amplitude's
// default is 10s, far too long for UX. On abandon we log a monitorable warning (and a RUM error) and
// resolve loading to false; the SDK keeps retrying in the background, so a later refetch can recover.
const FLAG_FETCH_TIMEOUT_MS = 3000;

export function createExperimentClient(): ExperimentClient {
  // Do NOT cache at module level. The caller owns the lifetime.
  const client = Experiment.initialize(NEXT_PUBLIC_AMPLITUDE_EXPERIMENTS_KEY, {
    fetchOnStart: false, // we’ll control fetching
    automaticExposureTracking: false,
    // Bound the SDK's own fetch so it can't block far beyond our budget (default is 10s).
    fetchTimeoutMillis: FLAG_FETCH_TIMEOUT_MS,
    exposureTrackingProvider: {
      // Track routes this through our custom exposure endpoint to attach additional context to the flag.
      track: async (exposure) => {
        try {
          const currentUser = client.getUser();
          const response = await fetch('/experiments/expose', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              currentUser,
              exposure,
            }),
          });

          if (response.ok && process.env.NEXT_PUBLIC_DEPLOYMENT_DOMAIN !== 'production') {
            console.log({ response }, 'Exposure tracked successfully');
          }
        } catch {
          // Do nothing
          // We track these logs server side
        }
      },
    },
  });

  return client;
}

/**
 * Minimal identity the flag SDK needs for user/email targeting, sourced independently of route
 * auth-gating so it is available on routes where the rich profile isn't loaded (/login, App Router).
 */
type FlagIdentity = {
  userId: string;
  email: string | null;
  /** Only present when the rich profile is loaded; absent on the Firebase-session fallback. */
  workspaces?: string[];
};

// Build the Experiment user from a resolved identity. Always carries a device_id so anonymous users
// bucket; adds user_id/email when we know who the user is. Passing no identifier would make every
// user/email-targeted flag fall back to its default.
const transformExperimentUser = (identity: FlagIdentity | null): ExperimentUser => ({
  device_id: getOrSetViewerId(),
  ...(identity
    ? {
        user_id: identity.userId,
        user_properties: {
          ...(identity.email ? { email: identity.email } : {}),
          ...(identity.workspaces ? { workspaces: identity.workspaces } : {}),
        },
        library: `WebDashboard/${NEXT_PUBLIC_RELEASE_VERSION}/${DEPLOYMENT_DOMAIN}`,
        platform: 'Web',
      }
    : {}),
});

/**
 * Tracks the Firebase auth session so flags can evaluate with the real user identity even on routes
 * where the route-gated user profile isn't loaded (/login, most onboarding, the App Router root). Without
 * it the Experiment user is anonymous on those routes and every user/email-targeted flag (e.g.
 * `stytch-password`) silently falls back to its default.
 */
function useFirebaseFlagIdentity(): FlagIdentity | null {
  // Seed null rather than reading auth.currentUser here: this initializer runs during render, including
  // on the server where `auth` is undefined (it's only initialised in the browser), which would throw and
  // break SSG prerendering. onIdTokenChanged fires immediately on subscribe, so the effect populates the
  // identity on the first client tick — and both server and client start from null, avoiding a hydration
  // mismatch.
  const [identity, setIdentity] = useState<FlagIdentity | null>(null);
  useEffect(
    () =>
      onIdTokenChanged(auth, (user) =>
        setIdentity(user ? { userId: user.uid, email: user.email } : null),
      ),
    [],
  );
  return identity;
}

type ExperimentStatus = 'loading' | 'refetching' | 'ready' | 'error';

type ExperimentClientState = {
  client: ExperimentClient;
  status: ExperimentStatus;
};

// biome-ignore lint/style/noNonNullAssertion: historic
export const AmplitudeExperimentsContext = createContext<ExperimentClientState>(undefined!);

export const AmplitudeExperimentsProvider: FC<{
  userProfileDetails: UserDetails | null;
  children: ReactNode;
}> = ({ children, userProfileDetails }) => {
  const [status, setStatus] = useState<ExperimentStatus>('loading');

  const clientRef = useRef<ExperimentClient>(undefined);
  if (!clientRef.current) {
    clientRef.current = createExperimentClient();
  }

  // biome-ignore lint/style/noNonNullAssertion: historic
  const client = clientRef.current!;

  // Prefer the rich profile (carries workspaces); fall back to the Firebase session so identity is
  // present on routes where the profile isn't loaded. This is what makes user/email-targeted flags
  // resolve on /login and in the App Router instead of evaluating anonymously.
  const firebaseIdentity = useFirebaseFlagIdentity();
  const workspaceId = userProfileDetails?.currentWorkspace?.id;
  const identity: FlagIdentity | null = userProfileDetails
    ? {
        userId: userProfileDetails.id,
        email: userProfileDetails.email,
        // Single-element array so bucketing is by workspace only — every member
        // of a workspace sends the same value and lands in the same variant.
        // Must match the server evaluator (`modules/server-utils/feature-flags.ts`).
        workspaces: [...new Set([userProfileDetails.currentWorkspace.id, userProfileDetails.id])],
      }
    : firebaseIdentity;

  // Refetch on identity change, not just mount, so a mid-session login/registration re-evaluates flags
  // instead of keeping the anonymous result. Keyed on identity fields, not the per-render object ref.
  // biome-ignore lint/correctness/useExhaustiveDependencies: key on identity fields, not the object ref
  useEffect(() => {
    let cancelled = false;
    // Re-run = identity changed; show loading so consumers re-read the new variant.
    setStatus((current) => (current === 'ready' ? 'refetching' : current));
    const user = transformExperimentUser(identity);
    let timeout: ReturnType<typeof setTimeout> | undefined;
    (async () => {
      try {
        // Race the fetch against a hard deadline so Amplitude can never block the UI beyond the budget,
        // regardless of the SDK's internal timeout/retry behaviour.
        await Promise.race([
          client.fetch(user),
          new Promise<never>((_, reject) => {
            timeout = setTimeout(
              () => reject(new Error('flag_fetch_timeout')),
              FLAG_FETCH_TIMEOUT_MS,
            );
          }),
        ]);
        if (!cancelled) {
          setStatus('ready');
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
        // Abandon to the fallback rather than blocking the UI, and emit a monitorable signal. The SDK
        // keeps retrying in the background, so a later refetch (identity change / navigation) can recover.
        flagLogger.warn('Amplitude flag fetch abandoned; using fallbacks', { error });
        datadogRum?.addError(error, { module: 'feature-flag', reason: 'fetch_abandoned' });
        setStatus('error');
      } finally {
        if (timeout) {
          clearTimeout(timeout);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [identity?.userId, identity?.email, workspaceId, client]);

  return (
    <AmplitudeExperimentsContext.Provider
      value={{
        status,
        client,
      }}
    >
      {children}
    </AmplitudeExperimentsContext.Provider>
  );
};

export function useFeatureFlag<T extends keyof FeatureFlagVariant>(
  name: T,
  fallback: FeatureFlagVariant[T],
  /* Whether or not to automatically fire an exposure event when this hook is used.
   * This is useful when you want evaluate a flag early, and only want to expose in certain situations
   * make sure not to use the variant as a condition for exposure, unset === true
   */
  autoExpose?: boolean,
): [FeatureFlagVariant[T], FeatureFlagMetaData] {
  const { status, client } = useContext(AmplitudeExperimentsContext);

  const [variant, setVariant] = useState<FeatureFlagVariant[T] | null>(null);
  const [isVariantReady, setIsVariantReady] = useState(false);

  const [override, setOverride] = useState<FeatureFlagVariant[T] | undefined>(undefined);
  useEffect(() => {
    if (!flagOverridesEnabled()) {
      return;
    }
    const value = getEffectiveOverrides(getCookie(FLAG_OVERRIDE_COOKIE) as string | undefined)[
      name
    ];
    if (value !== undefined) {
      console.info('[flag-overrides] Applying local override', { flagKey: name, value });
      setOverride(value as FeatureFlagVariant[T]);
    }
  }, [name]);

  const shouldAutoExpose = autoExpose ?? true;
  const loading = status === 'loading' || status === 'refetching' || !isVariantReady;

  const expose = useCallback(() => {
    if (status === 'ready') {
      client.exposure(name);
    }
  }, [status, client, name]);

  useEffect(() => {
    if (status === 'ready') {
      const variant = client.variant(name).value as FeatureFlagVariant[T];

      setVariant(variant);
      setIsVariantReady(true);

      // Expose variant unless disabled or no variant
      if (shouldAutoExpose && variant) {
        client.exposure(name);
      }

      // Track feature flag evaluation in Datadog RUM if not fallback
      if (variant) {
        datadogRum?.addFeatureFlagEvaluation(name, variant);
      }

      // Log evaluation details in development
      if (process.env.NEXT_PUBLIC_DEPLOYMENT_DOMAIN !== 'production') {
        console.log(
          {
            flagKey: name,
            variant,
            fallback,
            evaluatedVariant: variant ?? fallback,
          },
          'Feature flag evaluated',
        );
      }
    } else if (status === 'error') {
      // Fetch failed after retries — stop blocking on it so consumers render with the fallback instead
      // of hanging on `loading` forever.
      setVariant(null);
      setIsVariantReady(true);
    } else {
      setIsVariantReady(false);
    }
  }, [status, name, client, shouldAutoExpose, fallback]);

  if (override !== undefined) {
    return [override, { loading: false, expose: () => undefined }];
  }

  return [variant ?? fallback, { loading, expose }];
}
